/**
 * em-video-perf.js — page-wide video loading strategy (Mark, 27 Aug 2026).
 *
 * The home SPA was transferring ~44MB, almost all of it video: dozens of
 * autoplay clips mounted eagerly, each pulling a full-fat MP4 whether or not
 * anyone ever looked at it, and several rendered as blank white voids until
 * they loaded. None of that needs a re-encode to fix — Shopify already
 * transcodes every uploaded video into an MP4 ladder (480p/720p/1080p) plus
 * an HLS stream. The theme just wasn't asking for the right one, at the
 * right time. This layer does three things, cheapest-first:
 *
 *  1. DISTANCE-TIERED LOADING. Far from the viewport a video holds no src at
 *     all (zero bytes). Near it (NEAR_PX) it loads metadata only — enough to
 *     paint frame one, which doubles as the poster and kills the white
 *     voids, for a few KB. On screen it plays. Scrolled away it pauses;
 *     scrolled far away it drops its src again and reclaims the buffer.
 *     This is where the bulk of the saving is: bytes never requested.
 *
 *  2. RIGHT-SIZED RENDITION. Shopify's rendition tag is swappable in-place
 *     (same trailing id): a tile rendered 180px wide takes the 480p file,
 *     not the 720p one — typically 40% off with nothing visible at that
 *     size. Sized off the element's real layout box and DPR, and it only
 *     ever steps DOWN, never up. Any 404 falls straight back to the
 *     original URL, so a rendition that doesn't exist costs a retry, never
 *     a broken player.
 *
 *  3. HLS FOR BIG PLAYERS ON NATIVE-HLS BROWSERS (iOS Safari — most of this
 *     audience). Adaptive bitrate: the phone picks the rung its connection
 *     can afford, instead of everyone dragging the same fixed MP4. Errors
 *     fall back to the MP4.
 *
 * Deliberately conservative about quality: downgrades only when the maths
 * says the smaller rendition still covers the element's physical pixels.
 * Opt out per element with data-em-vp-skip.
 */
(function () {
  'use strict';
  if (window.__emVideoPerf) return;

  var NEAR_PX = 1200;        // preload metadata (paints frame 1) within this
  var FAR_PX = 2500;         // beyond this, drop the src entirely
  var MAX_PLAYING = 6;       // concurrent players, newest-visible wins
  var DPR_CAP = 2;           // retina is plenty; 3x buys nothing on video

  // Shopify rendition ladder. width = the SHORT side (portrait clips are
  // 270x480 / 404x720 / 606x1080), which is what a rendered box is measured
  // against.
  var LADDER = [
    { tag: 'SD-480p-0.9Mbps', w: 270 },
    { tag: 'HD-720p-1.6Mbps', w: 404 },
    { tag: 'HD-1080p-2.5Mbps', w: 606 }
  ];
  var RE_SHOPIFY = /^(https?:\/\/cdn\.shopify\.com\/videos\/c\/vp\/[^\/]+\/[^\/]+\.)((?:SD|HD)-\d+p-[\d.]+Mbps)(-\d+\.mp4.*)$/;

  function ladderIndex(tag) {
    for (var i = 0; i < LADDER.length; i++) if (LADDER[i].tag === tag) return i;
    return -1;
  }

  // Smallest rendition that still covers the element's physical width.
  // Never upgrades: if the theme asked for 480p we don't "improve" it to
  // 720p and spend bytes the design never asked for.
  function rightSized(url, el) {
    var m = RE_SHOPIFY.exec(url || '');
    if (!m) return url;
    var cur = ladderIndex(m[2]);
    if (cur <= 0) return url;                       // unknown tag, or already smallest
    var box = el.getBoundingClientRect();
    var cssW = box.width || el.offsetWidth || 0;
    if (!cssW) return url;                          // unmeasurable — leave it alone
    var needed = cssW * Math.min(window.devicePixelRatio || 1, DPR_CAP);
    for (var i = 0; i < cur; i++) {
      // 0.9 tolerance: a hair of upscale is invisible on video, and it is
      // the difference between a 180px tile taking 480p vs 720p.
      if (LADDER[i].w >= needed * 0.9) return m[1] + LADDER[i].tag + m[3];
    }
    return url;
  }

  function hlsUrl(url) {
    var m = RE_SHOPIFY.exec(url || '');
    if (!m) return null;
    return m[1].replace(/\.$/, '') + '.m3u8';
  }

  var canHls = (function () {
    try {
      var v = document.createElement('video');
      return !!(v.canPlayType && v.canPlayType('application/vnd.apple.mpegurl'));
    } catch (e) { return false; }
  })();

  // ── per-video state ────────────────────────────────────────────────────
  // tier 0 = no src, 1 = metadata only, 2 = playing
  function st(v) {
    if (!v.__emvp) v.__emvp = { tier: -1, orig: '', tried: 0 };
    return v.__emvp;
  }

  function originalSrc(v) {
    var s = st(v);
    if (s.orig) return s.orig;
    s.orig = v.getAttribute('data-em-vp-src') || v.getAttribute('src')
      || (v.querySelector('source') && v.querySelector('source').getAttribute('src')) || '';
    return s.orig;
  }

  // Detach the src so the browser abandons the download and frees the
  // buffer. Order matters: removeAttribute then load() or Safari keeps the
  // old media resource alive.
  function unload(v) {
    var s = st(v);
    if (s.tier === 0) return;
    try {
      v.pause();
      v.removeAttribute('src');
      var srcEl = v.querySelector('source');
      if (srcEl) srcEl.removeAttribute('src');
      v.load();
    } catch (e) {}
    s.tier = 0;
  }

  function applySrc(v, tier) {
    var s = st(v);
    var orig = originalSrc(v);
    if (!orig) return;
    var want = rightSized(orig, v);
    // Big player on an HLS-capable browser: let the device adapt.
    var box = v.getBoundingClientRect();
    if (canHls && (box.width || 0) >= 300 && !s.hlsFailed) {
      var h = hlsUrl(orig);
      if (h) want = h;
    }
    if (v.getAttribute('src') !== want) {
      v.setAttribute('src', want);
      var srcEl = v.querySelector('source');
      if (srcEl) srcEl.setAttribute('src', want);
      try { v.load(); } catch (e) {}
    }
    v.preload = tier >= 2 ? 'auto' : 'metadata';
    s.tier = tier;
  }

  // A rendition or manifest that does not exist must never cost a player:
  // step back to the untouched URL the theme shipped.
  function wireFallback(v) {
    if (v.__emvpErr) return;
    v.__emvpErr = true;
    v.addEventListener('error', function () {
      var s = st(v);
      if (s.tried >= 1) return;
      s.tried = 1;
      s.hlsFailed = true;
      var orig = originalSrc(v);
      if (!orig || v.getAttribute('src') === orig) return;
      v.setAttribute('src', orig);
      var srcEl = v.querySelector('source');
      if (srcEl) srcEl.setAttribute('src', orig);
      try { v.load(); if (s.tier >= 2) v.play().catch(function () {}); } catch (e) {}
    }, true);
  }

  // ── playback budget ────────────────────────────────────────────────────
  var visible = [];   // most-recently-visible last
  function enforceBudget() {
    var playing = visible.filter(function (v) { return document.contains(v); });
    if (playing.length <= MAX_PLAYING) return;
    playing.slice(0, playing.length - MAX_PLAYING).forEach(function (v) {
      try { v.pause(); } catch (e) {}
    });
  }
  function markVisible(v) {
    var i = visible.indexOf(v);
    if (i >= 0) visible.splice(i, 1);
    visible.push(v);
    enforceBudget();
  }
  function markHidden(v) {
    var i = visible.indexOf(v);
    if (i >= 0) visible.splice(i, 1);
  }

  // ── observers ──────────────────────────────────────────────────────────
  // Two rings: NEAR (metadata) and IN-VIEW (play). Both are cheap; the
  // distance work happens in the callbacks, not on scroll.
  var ioNear = null, ioView = null;

  function onNear(entries) {
    entries.forEach(function (en) {
      var v = en.target;
      if (en.isIntersecting) {
        if (st(v).tier < 1) applySrc(v, 1);
      } else if (st(v).tier > 0) {
        // Only reclaim once genuinely far — FAR ring is the outer margin.
        unload(v);
      }
    });
  }

  function onView(entries) {
    entries.forEach(function (en) {
      var v = en.target;
      if (en.isIntersecting) {
        if (st(v).tier < 2) applySrc(v, 2);
        markVisible(v);
        if (v.autoplay || v.hasAttribute('autoplay') || v.getAttribute('data-em-vp-auto') === '1') {
          var pr = v.play();
          if (pr && pr.catch) pr.catch(function () {});
        }
      } else {
        markHidden(v);
        try { v.pause(); } catch (e) {}
        if (st(v).tier > 1) st(v).tier = 1;      // keep the buffer, stop the spend
      }
    });
  }

  function ensureObservers() {
    if (ioNear || !('IntersectionObserver' in window)) return;
    ioNear = new IntersectionObserver(onNear, { rootMargin: FAR_PX + 'px 0px ' + FAR_PX + 'px 0px' });
    ioView = new IntersectionObserver(onView, { rootMargin: '0px', threshold: 0.01 });
  }

  function govern(v) {
    if (!v || v.__emvpOn) return;
    if (v.hasAttribute('data-em-vp-skip')) return;
    v.__emvpOn = true;
    originalSrc(v);
    v.setAttribute('data-em-vp-src', st(v).orig);
    wireFallback(v);
    v.setAttribute('playsinline', '');
    // autoplay stays as an intent flag we honour ourselves — leaving the
    // attribute on would let the browser start fetching the moment a src
    // appears, which is the behaviour we are removing.
    if (v.hasAttribute('autoplay')) {
      v.setAttribute('data-em-vp-auto', '1');
      v.removeAttribute('autoplay');
      v.autoplay = false;
    }
    v.preload = 'none';

    // Already on screen (a lazy tile system just mounted it): leave its src
    // alone and let it run — stripping it here would restart a download
    // that is already most of the way home.
    var r = v.getBoundingClientRect();
    var onScreen = r.top < window.innerHeight && r.bottom > 0 && r.width > 0;
    if (onScreen) {
      st(v).tier = 2;
      if (v.readyState === 0) applySrc(v, 2);      // nothing decoded yet — safe to right-size
      markVisible(v);
    } else {
      unload(v);
    }
    ensureObservers();
    if (ioNear) { ioNear.observe(v); ioView.observe(v); }
  }

  function sweep(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var list = scope.querySelectorAll ? scope.querySelectorAll('video') : [];
    for (var i = 0; i < list.length; i++) govern(list[i]);
    if (root && root.tagName === 'VIDEO') govern(root);
  }

  // Videos arrive continuously here — carousels, the SPA swap, the drawer,
  // the UGC strips. Observe the document rather than chasing each system.
  function watch() {
    try {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'VIDEO') govern(n);
            else if (n.querySelectorAll) sweep(n);
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  function boot() {
    ensureObservers();
    sweep(document);
    watch();
    window.addEventListener('em:v2:swapped', function () { setTimeout(function () { sweep(document); }, 300); });
  }

  window.__emVideoPerf = {
    sweep: sweep,
    stats: function () {
      var vids = document.querySelectorAll('video');
      var out = { total: vids.length, noSrc: 0, metadata: 0, playing: 0, hls: 0, downgraded: 0 };
      for (var i = 0; i < vids.length; i++) {
        var v = vids[i], s = st(v), src = v.getAttribute('src') || '';
        if (!src) out.noSrc++;
        else if (s.tier >= 2) out.playing++;
        else out.metadata++;
        if (/\.m3u8/.test(src)) out.hls++;
        if (s.orig && src && src !== s.orig) out.downgraded++;
      }
      return out;
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
